import React, { useState, useContext } from "react"
import { ThemeProvider, createTheme, styled } from "@mui/material/styles"
import { Box } from "@mui/material"
import { useNavigate } from "react-router-dom"
import Header from "../../layouts/Header"
import BackIcon from "../../components/BackIcon"
import CustomConditionalActionsTableServer from "../../components/CustomConditionalActionsTableServerSide"
import ModalImportCSV from "../../components/CustomCSVImportButton"
import { GlobalContext } from "../../contexts/GlobalContext"

// Importaciones para las acciones dinámicas, mutaciones y exportaciones
import { useQueryClient } from "@tanstack/react-query"
import { useMutation, api } from "../../api"
import getIconComponent from "../utils/getIconComponent"
import CustomBackdrop from "../../components/CustomBackdrop"
import {
  handleExportDataPdfLGScreen,
  handleExportDataPdfSMScreen,
  handleAllExportDataCSV,
} from "../utils/reactTableActions/exportToolbarActions"

// Estilos base para el layout principal (mismo que TiposPredio)
const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(8),
  flexGrow: 1,
  padding: "0 16px",
  height: "100vh",
}))

// Tema estándar de SIACDEV1.0
const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" } },
})

const TiposPPE = () => {
  const qc = useQueryClient()
  const navigate = useNavigate()
  const { selectedMenuInfo } = useContext(GlobalContext)

  // Estado para controlar la apertura del modal de importación
  const [openImportModal, setOpenImportModal] = useState(false)

  // Mutación para eliminación con recarga de grilla
  const { mutateAsync: SaveEliminacionTipoActivo, isPending: isDeletingTipoActivo } = useMutation({
    queryKey: ["isDeletingTipoActivo"],
    fn: async (data) => {
      const response = await api.post("/TiposPPE/eliminarTiposPPE", data)
      return response.data
    },
    showError: "modal",
    showSuccess: "toast",
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["TiposPPE"] })
    },
  })

  // Columnas a mostrar en la tabla principal
  const columnsTable = [
    { accessorKey: "tipcodigo", header: "Código", size: 100 },
    { accessorKey: "tipdescri", header: "Descripción", size: 300 },
    { accessorKey: "tipaniodep", header: "Años Dep.", size: 120 },
    { accessorKey: "tipmesdep", header: "Meses Dep.", size: 120 },
    {
      accessorKey: "tipporresidual",
      header: "% Residual",
      size: 130,
      Cell: ({ cell }) => `${cell.getValue()}%`,
    },
    {
      accessorKey: "tipstatus",
      header: "Estado",
      size: 120,
      Cell: ({ cell }) => (cell.getValue() === "A" ? "ACTIVO" : "INACTIVO"),
    },
  ]

  // Configuración de validación para el CSV (columnas clave según la tabla afbtip)
  const fieldConfigs = {
    tipcodigo: { required: true, key: true },
    tipdescri: { required: true },
    tipaniodep: { required: false },
    tipmesdep: { required: false },
    tipporresidual: { required: false },
    tipctadepre: { required: false },
    tipctagasdep: { required: false },
    numsecini: { required: false },
    numseccont: { required: false },
    tippervar: { required: false },
    tipinidepre: { required: false },
    tipanioutilsri: { required: false },
    tipmesutilsri: { required: false },
    tipstatus: { required: false },
    tipctacon: { required: false },
    tipproduccion: { required: false },
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            margin: "0 30px 30px 30px",
            fontSize: "25px",
          }}
        >
          <b>Gestión de Tipos de Activos (PPE)</b>
        </div>

        <CustomBackdrop isLoading={isDeletingTipoActivo} />

        <StyledRoot>
          {/* Modal de Importación CSV integrado */}
          <ModalImportCSV
            open={openImportModal}
            onClose={() => setOpenImportModal(false)}
            templateFileName="Plantilla_TiposActivosPPE.csv"
            fieldConfigs={fieldConfigs}
            maxFileSize={10 * 1024 * 1024}
            validateEndpoint="/TiposPPE/validarTiposPPEIMP"
            insertEndpoint="/TiposPPE/insertarTiposPPEIMP"
            onImportComplete={() => qc.invalidateQueries({ queryKey: ["TiposPPE"] })}
          />

          <CustomConditionalActionsTableServer
            endpoint="/TiposPPE/getAllTiposPPE"
            errorMsgFilterSearch="Error en cargar datos"
            queryKeyModal="TiposPPE"
            perPage={10}
            rowActionsWidthTable={120}
            columnsTable={columnsTable}
            // ACCIONES DE FILA (Editar y Eliminar)
            rowActions={(row) => {
              const editarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) => action?.acccaption === "EDITAR",
              )
              const eliminarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) => action?.acccaption === "ELIMINAR",
              )

              const actions = [
                {
                  label: editarAction?.acccaption || "Editar",
                  key: editarAction?.acccaption || "EDITAR",
                  icon: getIconComponent(editarAction?.accnameicono, editarAction?.acctipoico),
                  onClick: (row) => {
                    navigate("editar", { state: row.original })
                  },
                },
                {
                  label: eliminarAction?.acccaption || "Eliminar",
                  key: eliminarAction?.acccaption || "ELIMINAR",
                  icon: getIconComponent(eliminarAction?.accnameicono, eliminarAction?.acctipoico),
                  onClick: async (row) => {
                    try {
                      await SaveEliminacionTipoActivo(row.original)
                    } catch (err) {
                      console.error("Error eliminando el tipo de activo:", err)
                    }
                  },
                },
              ]
              return actions
            }}
            // BARRA DE HERRAMIENTAS SUPERIOR (Crear, Exportar, Importar)
            topToolbarCustomActions={({ table, device }) => {
              const crearAction = selectedMenuInfo?.data?.barraAcciones?.find((action) => action.acccaption === "CREAR")
              const exportarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                (action) => action.acccaption === "EXPORTAR",
              )

              const toolbarActions = [
                {
                  label: crearAction?.acccaption || "Crear",
                  key: crearAction?.acccaption || "CREAR",
                  icon: getIconComponent(crearAction?.accnameicono, crearAction?.acctipoico),
                  onClick: () => {
                    navigate("crear")
                  },
                },
                {
                  type: "dropdown",
                  label: exportarAction?.acccaption || "Exportar",
                  key: "exportarDropdown",
                  icon: getIconComponent(exportarAction?.accnameicono, exportarAction?.acctipoico),
                  actions: [
                    {
                      label: "Exportar PDF",
                      key: "exportarPDF",
                      icon: getIconComponent(exportarAction?.accnameicono, exportarAction?.acctipoico),
                      onClick: ({ columns, data }) => {
                        if (device === "sm") {
                          return handleExportDataPdfSMScreen(
                            columns,
                            data,
                            "Reporte de Tipos de Activos",
                            `Reporte_Tipos_Activos_${new Date().toLocaleString()}`,
                          )
                        }
                        handleExportDataPdfLGScreen(
                          columns,
                          table.getCoreRowModel().rows,
                          "Reporte de Tipos de Activos",
                          `Reporte_Tipos_Activos_${new Date().toLocaleString()}`,
                        )
                      },
                    },
                    {
                      label: "Exportar CSV",
                      key: "exportarCSV",
                      icon: getIconComponent(exportarAction?.accnameicono, exportarAction?.acctipoico),
                      onClick: ({ data }) => {
                        handleAllExportDataCSV(data, `Reporte_Tipos_Activos_${new Date().toLocaleString()}`)
                      },
                    },
                  ],
                },
                {
                  label: "Importar",
                  key: "importarDropdown",
                  icon: getIconComponent(exportarAction?.accnameicono, exportarAction?.acctipoico),
                  onClick: () => {
                    setOpenImportModal(true)
                  },
                },
              ]
              return toolbarActions
            }}
          />
        </StyledRoot>
      </div>
    </ThemeProvider>
  )
}

export default TiposPPE
